import { useState, type ComponentProps, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { Link } from "react-router";
import { formatEur, formatEurPlain, formatEurSigned, formatMonthTitle } from "@/lib/format";
import { IconAlert, IconChevronLeft, IconChevronRight } from "./icons";

export function PageHeader({ title, description, actions }: { title: ReactNode; description?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        {description ? <p className="mt-1 max-w-prose text-muted">{description}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
    </header>
  );
}

export function Panel({ title, action, children, className = "" }: { title?: ReactNode; action?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={`rounded-xl border border-line bg-surface p-4 md:p-5 ${className}`}>
      {title || action ? (
        <div className="mb-3 flex items-baseline justify-between gap-3">
          {title ? <h2 className="font-semibold">{title}</h2> : <span />}
          {action}
        </div>
      ) : null}
      {children}
    </section>
  );
}

/** A labelled amount. `tone` colours only a small marker, never the number (text stays ink). */
export function Figure({
  label,
  amount,
  signed = false,
  hint,
  size = "md",
}: {
  label: string;
  amount: string;
  signed?: boolean;
  hint?: ReactNode;
  size?: "md" | "lg";
}) {
  return (
    <div className="min-w-0">
      <p className="text-sm text-muted">{label}</p>
      <p className={`amount font-semibold tracking-tight ${size === "lg" ? "text-4xl" : "text-xl"}`}>
        {signed ? formatEurSigned(amount) : formatEurPlain(amount)}
      </p>
      {hint ? <p className="text-sm text-muted">{hint}</p> : null}
    </div>
  );
}

export function Amount({ value, signed = false, className = "" }: { value: string; signed?: boolean; className?: string }) {
  return <span className={`amount ${className}`}>{signed ? formatEurSigned(value) : formatEur(value)}</span>;
}

/** Previous / current / next navigation for accounting months or years. */
export function PeriodSwitcher({
  label,
  previousHref,
  nextHref,
  previousLabel,
  nextLabel,
  children,
}: {
  label: string;
  previousHref: string;
  nextHref: string;
  previousLabel: string;
  nextLabel: string;
  children?: ReactNode;
}) {
  return (
    <nav aria-label="Periodo" className="mb-5 flex items-center justify-between gap-2">
      <Link to={previousHref} aria-label={previousLabel} className="flex size-11 items-center justify-center rounded-full hover:bg-surface">
        <IconChevronLeft />
      </Link>
      <div className="text-center">
        <p className="font-ledger text-3xl leading-tight text-petrol-strong">{label}</p>
        {children}
      </div>
      <Link to={nextHref} aria-label={nextLabel} className="flex size-11 items-center justify-center rounded-full hover:bg-surface">
        <IconChevronRight />
      </Link>
    </nav>
  );
}

export function MonthTitle({ month }: { month: string }) {
  return <>{formatMonthTitle(month)}</>;
}

export function Notice({ tone = "info", children }: { tone?: "info" | "error" | "success"; children: ReactNode }) {
  const styles = {
    info: "bg-petrol-soft text-ink",
    error: "bg-over-soft text-over",
    success: "bg-[#e3efe7] text-under",
  }[tone];
  return (
    <div role={tone === "error" ? "alert" : "status"} className={`flex gap-2 rounded-lg px-4 py-3 text-sm ${styles}`}>
      {tone === "error" ? <IconAlert className="mt-0.5 shrink-0" width={18} height={18} /> : null}
      <div className="min-w-0">{children}</div>
    </div>
  );
}

export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="rounded-xl border border-dashed border-line bg-surface px-4 py-8 text-center">
      <p className="font-medium">{title}</p>
      {children ? <div className="mt-1 text-sm text-muted">{children}</div> : null}
    </div>
  );
}

const fieldClass =
  "mt-1 w-full rounded-lg border border-line bg-surface px-3 py-3 text-base text-ink focus:border-petrol focus:outline-none disabled:bg-ground disabled:text-muted";

export function Field({ label, hint, error, children }: { label: string; hint?: ReactNode; error?: string | null; children: ReactNode }) {
  return (
    <label className="block">
      <span className="font-medium">{label}</span>
      {hint ? <span className="block text-sm text-muted">{hint}</span> : null}
      {children}
      {error ? <span className="mt-1 block text-sm text-over">{error}</span> : null}
    </label>
  );
}

export const TextInput = (props: ComponentProps<"input">) => <input {...props} className={`${fieldClass} ${props.className ?? ""}`} />;
export const SelectInput = (props: SelectHTMLAttributes<HTMLSelectElement>) => <select {...props} className={`${fieldClass} ${props.className ?? ""}`} />;
export const TextArea = (props: TextareaHTMLAttributes<HTMLTextAreaElement>) => <textarea rows={2} {...props} className={`${fieldClass} ${props.className ?? ""}`} />;

type ButtonVariant = "primary" | "secondary" | "danger" | "ghost";
const buttonStyles: Record<ButtonVariant, string> = {
  primary: "bg-petrol text-white hover:bg-petrol-strong",
  secondary: "border border-line bg-surface text-ink hover:bg-ground",
  danger: "bg-over text-white hover:brightness-95",
  ghost: "text-petrol hover:bg-petrol-soft",
};

export function Button({
  variant = "primary",
  className = "",
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant }) {
  return (
    <button
      type="button"
      {...props}
      className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-lg px-4 py-2.5 font-semibold disabled:opacity-60 ${buttonStyles[variant]} ${className}`}
    />
  );
}

/** Two-step confirmation for destructive actions (no browser dialogs). */
export function ConfirmButton({
  label,
  confirmLabel,
  onConfirm,
  variant = "danger",
  disabled,
}: {
  label: string;
  confirmLabel: string;
  onConfirm: () => void | Promise<void>;
  variant?: ButtonVariant;
  disabled?: boolean;
}) {
  const [armed, setArmed] = useState(false);
  if (!armed) {
    return (
      <Button variant="secondary" onClick={() => setArmed(true)} disabled={disabled}>
        {label}
      </Button>
    );
  }
  return (
    <span className="inline-flex flex-wrap gap-2">
      <Button
        variant={variant}
        onClick={async () => {
          await onConfirm();
          setArmed(false);
        }}
      >
        {confirmLabel}
      </Button>
      <Button variant="ghost" onClick={() => setArmed(false)}>
        Annulla
      </Button>
    </span>
  );
}

export function Badge({ children, tone = "neutral" }: { children: ReactNode; tone?: "neutral" | "petrol" | "warn" }) {
  const styles = { neutral: "bg-ground text-muted", petrol: "bg-petrol-soft text-petrol-strong", warn: "bg-over-soft text-over" }[tone];
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${styles}`}>{children}</span>;
}
